import { ChartOptions } from "chart.js";
import { Line } from "react-chartjs-2";
import { formatDateForWidgets } from "../../../utils";
import { TestTimeData, ScoreGraphData, TimeWidgetProps } from "./dataTypes";

const TimeWidget = ({ timeData }: TimeWidgetProps) => {
    const options: ChartOptions<"line"> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: "bottom",
          
          labels: {
            usePointStyle: false,
            font: {
              size: 12,
              family: "Gully",
            },
            boxWidth: 14,
            boxHeight: 14,
            textAlign: "center",
            generateLabels: (chart) => {
              return chart.data.datasets.map((dataset, index) => ({
                text: dataset.label || "Unnamed Dataset",
                fillStyle: dataset.borderColor as string,
                hidden: !chart.isDatasetVisible(index),
                strokeStyle: dataset.borderColor as string,
                lineWidth: 1,
                datasetIndex: index,
              }));
            },
          },
        },
        tooltip: {
          callbacks: {
            title: function (tooltipItems) {
              const index = tooltipItems[0].dataIndex;
              const testName = `${timeData[index]?.name}` || "Unnamed Test";
              return testName;
            },
            label: function (tooltipItem) {
              return `${tooltipItem.dataset.label}: ${tooltipItem.raw} sec`;
            },
          },
          titleFont: {
            family: "Gully",
            size: 14,
            weight: "bold",
          },
          bodyFont: {
            family: "Gully",
            size: 12,
          },
          footerFont: {
            family: "Gully",
            size: 12,
            style: "italic",
          },
        },
        datalabels: {
          display: false,
        },
      },
      scales: {
        x: {
          offset: true,
          ticks: {
            color: "#000",
            align: "center",
            font:{
              size: 10,
              weight: "bold",
              family: "Gully",
            }
          },
          grid: {
            drawTicks: true,
            display: false,
          },
          title: {
            display: true,
            text: "Date",
            color: "#000",
            font: {
              size: 12,
              weight: "bold",
              family: "Gully",
            },
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            display: true,
          },
          title: {
            display: true,
            text: "Average time (seconds)",
            color: "#000",
            font: {
              size: 12,
              weight: "bold",
              family: "Gully",
            },
          },
          ticks:{
            font:{
              size: 10,
              weight: "bold",
              family: "Gully",
            }
          }
        },
      },
    };
  
    const generateData = (data: TestTimeData[]) => {
      let requiredData: ScoreGraphData = {
        labels: [],
        datasets: [],
      };
  
      if (!data) {
        return requiredData;
      }
  
      data.forEach((item) => {
        const formattedDate = formatDateForWidgets(item.testtakendate);
        requiredData.labels.push(formattedDate);
      });
  
      const avgreadingwritingtime = data.map((item) =>
        parseFloat((item.time.avgreadingwritingtime / 1000).toFixed(1))
      );
      const avgmathstime = data.map((item) =>
        parseFloat((item.time.avgmathstime / 1000).toFixed(1))
      );
      const overallavgtime = data.map((item) =>
        parseFloat((item.time.overallavgtime / 1000).toFixed(1))
      );
  
      requiredData.datasets = [
        {
          label: "R&W",
          data: avgreadingwritingtime,
          borderColor: "#FFBA08",
          borderWidth: 2,
          pointBackgroundColor: "#FFBA08",
          tension: 0.0,
          fill: false,
        },
        {
          label: "Math",
          data: avgmathstime,
          borderColor: "#47ACFF",
          borderWidth: 2,
          pointBackgroundColor: "#47ACFF",
          tension: 0.0,
          fill: false,
        },
        {
          label: "Overall",
          data: overallavgtime,
          borderColor: "#73EEDC",
          borderWidth: 2,
          pointBackgroundColor: "#73EEDC",
          tension: 0.0,
          fill: false,
        },
      ];
  
      return requiredData;
    };
  
    const chartData = generateData(timeData);
  
    return (
      <div
        className="w-full flex p-3 flex-col justify-between rounded-[18px] bg-satPrepTransWhite backdrop-blur-sevenFive border-[0.5px] border-satPrepGrey shadow-test-card"
      >
        <p className="text-center font-gully text-xl font-medium text-satPrepDarkAccent">
          Time spent per question
        </p>
        <div className="w-full min-h-[300px]">
          <Line data={chartData} options={options} />
        </div>
      </div>
    );
};

export default TimeWidget;